import React, {useMemo, useState} from 'react'

export interface CalendarEvent {
  uid?: string | number
  title: string
  start: Date
  end: Date
  location?: string | null
  description?: string | null
  sequence?: number
}

interface CalendarExportButtonProps {
  events: CalendarEvent[]
  fileName?: string
  label?: string
  className?: string
  size?: 'sm' | 'lg'
}

const pad = (value: number) => String(value).padStart(2, '0')

const normalizeText = (value?: string | null) => (value || '').trim()

const sanitizeFileName = (value: string) =>
  value
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase()

const formatUtcDateTime = (date: Date) =>
  `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}T${pad(
    date.getUTCHours()
  )}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}Z`

const formatGoogleDateTime = (date: Date) => formatUtcDateTime(date)

const escapeIcsText = (value?: string | null) =>
  normalizeText(value)
    .replace(/\\/g, '\\\\')
    .replace(/\r?\n/g, '\\n')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')

const foldIcsLine = (line: string) => {
  const chunks: string[] = []
  let current = line
  while (current.length > 73) {
    chunks.push(current.slice(0, 73))
    current = ` ${current.slice(73)}`
  }
  chunks.push(current)
  return chunks.join('\r\n')
}

const normalizeEvent = (event: CalendarEvent): CalendarEvent | null => {
  const start = event.start instanceof Date ? event.start : new Date(event.start)
  const end = event.end instanceof Date ? event.end : new Date(event.end)
  if (!normalizeText(event.title) || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return null
  }
  if (end <= start) {
    return null
  }
  return {
    ...event,
    uid: event.uid,
    title: normalizeText(event.title),
    start,
    end,
    location: normalizeText(event.location),
    description: normalizeText(event.description),
    sequence: typeof event.sequence === 'number' && Number.isFinite(event.sequence) ? event.sequence : 0,
  }
}

const buildIcs = (events: CalendarEvent[]) => {
  const now = formatUtcDateTime(new Date())
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//NOSI//Schedule Calendar Export//TR',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    ...events.flatMap((event, index) => {
      const uid = `${event.uid || `${event.start.getTime()}-${index}`}@nosi-calendar-export`
      return [
        'BEGIN:VEVENT',
        `UID:${escapeIcsText(uid)}`,
        `SEQUENCE:${event.sequence || 0}`,
        `DTSTAMP:${now}`,
        `LAST-MODIFIED:${now}`,
        `DTSTART:${formatUtcDateTime(event.start)}`,
        `DTEND:${formatUtcDateTime(event.end)}`,
        `SUMMARY:${escapeIcsText(event.title)}`,
        event.location ? `LOCATION:${escapeIcsText(event.location)}` : '',
        event.description ? `DESCRIPTION:${escapeIcsText(event.description)}` : '',
        'END:VEVENT',
      ].filter(Boolean)
    }),
    'END:VCALENDAR',
  ]

  return `${lines.map(foldIcsLine).join('\r\n')}\r\n`
}

const buildGoogleCalendarUrl = (event: CalendarEvent) => {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${formatGoogleDateTime(event.start)}/${formatGoogleDateTime(event.end)}`,
  })

  if (event.description) {
    params.set('details', event.description)
  }
  if (event.location) {
    params.set('location', event.location)
  }

  return `https://calendar.google.com/calendar/render?${params.toString()}`
}

const downloadIcs = (events: CalendarEvent[], fileName: string) => {
  const blob = new Blob([buildIcs(events)], {type: 'text/calendar;charset=utf-8'})
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${sanitizeFileName(fileName) || 'takvim'}.ics`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export const createCalendarDateTime = (dateText: string, timeText: string) => {
  const datePart = dateText.split('T')[0]
  const [year, month, day] = datePart.split('-').map(Number)
  const [hour = 0, minute = 0, second = 0] = timeText.split(':').map(Number)
  return new Date(year, month - 1, day, hour, minute, second)
}

export const CalendarExportButton: React.FC<CalendarExportButtonProps> = ({
  events,
  fileName = 'takvim',
  label = 'Takvime Aktar',
  className = '',
  size = 'sm',
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const normalizedEvents = useMemo(
    () => events.map(normalizeEvent).filter((event): event is CalendarEvent => !!event),
    [events]
  )

  const hasSingleEvent = normalizedEvents.length === 1
  const buttonSizeClass = size ? `btn-${size}` : ''

  if (!normalizedEvents.length) {
    return (
      <button type='button' className={`btn btn-light-primary ${buttonSizeClass} ${className}`} disabled>
        <i className='bi bi-calendar-plus me-1'></i>
        {label}
      </button>
    )
  }

  return (
    <div className={`btn-group ${className}`}>
      <button
        type='button'
        className={`btn btn-light-primary ${buttonSizeClass} dropdown-toggle`}
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
      >
        <i className='bi bi-calendar-plus me-1'></i>
        {label}
      </button>
      <ul className={`dropdown-menu dropdown-menu-end ${isOpen ? 'show' : ''}`}>
        {hasSingleEvent && (
          <li>
            <button
              type='button'
              className='dropdown-item'
              onClick={() => {
                setIsOpen(false)
                window.open(buildGoogleCalendarUrl(normalizedEvents[0]), '_blank', 'noopener')
              }}
            >
              Google Calendar
            </button>
          </li>
        )}
        <li>
          <button
            type='button'
            className='dropdown-item'
            onClick={() => {
              setIsOpen(false)
              downloadIcs(normalizedEvents, fileName)
            }}
          >
            Google / Apple / Outlook (.ics)
          </button>
        </li>
      </ul>
    </div>
  )
}
